import {
  LIBRO_COMPRAS_DOC_TYPES,
  siiDocTypeMeta,
} from "~/constants/siiDocTypes";

import type { LibroComprasRow } from "~/types/purchasing";

// Builds the SII Libro de Compras from the AP rows: re-signs the amounts, routes each document's
// amount into the right column (afecto / exento / IVA crédito), and groups by SII document type
// with a subtotal per group and a Total General — matching the layout Softland emits.

/** The five numeric columns of the Libro de Compras (plus the Total). */
export interface LibroComprasAmounts {
  netoAfecto: number;
  netoExento: number;
  ivaCredito: number;
  ivaNoRecuperable: number;
  ivaSupermercado: number;
  total: number;
}

export interface LibroComprasLine extends LibroComprasAmounts {
  id: string;
  siiCode: number | null;
  folio: string;
  /** Correlativo interno = número de comprobante del asiento (account_movements.move_number). */
  corrInterno: string;
  emissionDate: string;
  rut: string;
  razonSocial: string;
}

export interface LibroComprasSection extends LibroComprasAmounts {
  code: string;
  siiCode: number | null;
  title: string;
  lines: LibroComprasLine[];
}

export interface LibroComprasReport {
  sections: LibroComprasSection[];
  grandTotal: LibroComprasAmounts;
  docCount: number;
}

const ZERO: LibroComprasAmounts = {
  netoAfecto: 0,
  netoExento: 0,
  ivaCredito: 0,
  ivaNoRecuperable: 0,
  ivaSupermercado: 0,
  total: 0,
};

const addAmounts = (
  a: LibroComprasAmounts,
  b: LibroComprasAmounts
): LibroComprasAmounts => ({
  netoAfecto: a.netoAfecto + b.netoAfecto,
  netoExento: a.netoExento + b.netoExento,
  ivaCredito: a.ivaCredito + b.ivaCredito,
  ivaNoRecuperable: a.ivaNoRecuperable + b.ivaNoRecuperable,
  ivaSupermercado: a.ivaSupermercado + b.ivaSupermercado,
  total: a.total + b.total,
});

// AP stores facturas negative / NC positive (sub-mayor: negative = por pagar). The SII ledger uses
// the tax convention — facturas positive, NC negative — so flip every amount. Normalize -0 → 0 so
// zero-amount rows don't render as "$-0".
const flip = (n: number | null | undefined) => {
  const v = -Number(n ?? 0);
  return v === 0 ? 0 : v;
};

/** Route one AP row's amounts into the Libro de Compras columns (already re-signed). */
export function libroComprasAmounts(row: LibroComprasRow): LibroComprasAmounts {
  const total = flip(row.total_clp);
  if (row.doc_tipo_code === "DI") {
    // DIN (914): sólo se declara el IVA de la importación. Softland lo muestra en IVA Crédito,
    // en positivo, con Neto Afecto y Neto Exento en cero.
    return { ...ZERO, ivaCredito: total, total };
  }
  // FE / NC / ND / FC → afecto + IVA. FNE (exenta) trae el monto en `exento` (neto va en cero).
  return {
    netoAfecto: flip(row.neto),
    netoExento: flip(row.exento),
    ivaCredito: flip(row.iva),
    ivaNoRecuperable: 0,
    ivaSupermercado: 0,
    total,
  };
}

const toLine = (row: LibroComprasRow): LibroComprasLine => {
  const meta = siiDocTypeMeta(row.doc_tipo_code);
  return {
    id: row.id,
    siiCode: meta?.siiCode ?? null,
    folio: row.folio,
    corrInterno: row.corr_interno ?? "",
    emissionDate: row.emission_date,
    rut: row.rut,
    razonSocial: row.razon_social ?? "",
    ...libroComprasAmounts(row),
  };
};

const byDateThenFolio = (a: LibroComprasLine, b: LibroComprasLine) =>
  a.emissionDate.localeCompare(b.emissionDate) || a.folio.localeCompare(b.folio);

/**
 * Group the (already FEX-excluded) libro rows into SII sections in Softland's order, each with its
 * subtotal, plus the Total General. Unknown doc types fall into a trailing section keyed by their
 * raw code so nothing is silently dropped.
 */
export function buildLibroComprasReport(rows: LibroComprasRow[]): LibroComprasReport {
  const linesByCode = new Map<string, LibroComprasLine[]>();
  for (const row of rows) {
    const list = linesByCode.get(row.doc_tipo_code) ?? [];
    list.push(toLine(row));
    linesByCode.set(row.doc_tipo_code, list);
  }

  const sections: LibroComprasSection[] = [];

  const pushSection = (
    code: string,
    siiCode: number | null,
    title: string,
    lines: LibroComprasLine[]
  ) => {
    if (lines.length === 0) return;
    lines.sort(byDateThenFolio);
    const subtotal = lines.reduce(addAmounts, { ...ZERO });
    sections.push({ code, siiCode, title, lines, ...subtotal });
  };

  // Known SII types first, in the canonical order.
  for (const meta of LIBRO_COMPRAS_DOC_TYPES) {
    pushSection(
      meta.code,
      meta.siiCode,
      meta.sectionTitle,
      linesByCode.get(meta.code) ?? []
    );
    linesByCode.delete(meta.code);
  }

  // Anything left is an unknown code — keep it visible under its raw code.
  for (const [code, lines] of linesByCode) {
    pushSection(code, null, code, lines);
  }

  const grandTotal = sections.reduce(addAmounts, { ...ZERO });
  const docCount = sections.reduce((n, s) => n + s.lines.length, 0);

  return { sections, grandTotal, docCount };
}

/** Format a RUT as XX.XXX.XXX-X (with dots), matching the Softland libro. */
export function formatRutWithDots(rut: string | null | undefined): string {
  if (!rut) return "";
  const cleaned = rut.replace(/[^0-9kK]/g, "").toUpperCase();
  if (cleaned.length <= 1) return cleaned;
  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);
  const withDots = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${withDots}-${dv}`;
}
